import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useAccessData } from '../hooks/useAccessData.js'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import { useDeviceStatus } from '../hooks/useDeviceStatus.js'
import { useDeviceStatusCalculation } from '../hooks/useDeviceStatusCalculation.js'
import DevicePairingWizard from './DevicePairingWizard.jsx'

/**
 * Custom hook for handling click outside to close functionality
 * Similar to UserMenu.jsx implementation
 */
const useOutsideClick = (ref, isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose()
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [ref, isOpen, onClose])
}

/**
 * Device Selector component with consistent dropdown experience
 * - Always shows dropdown with styled chevron arrow that rotates when open
 * - Search functionality for multiple devices
 * - "Add Device" workflow accessible from dropdown
 */
function DeviceSelector() {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showPairingWizard, setShowPairingWizard] = useState(false)
  
  const { 
    devices, 
    selectedDeviceId, 
    selectDevice, 
    hasMultipleDevices,
    currentDeviceName,
    // setFleetView, // TODO: Implement Fleet Overview later
  } = useCloudContext()
  
  // Helper function to calculate online status for sorting (matches DeviceListItem logic)
  const calculateOnlineStatus = useCallback((device) => {
    if (!device.lastSeen) return false
    
    const now = Date.now()
    const lastSeenTime = device.lastSeen.toMillis ? device.lastSeen.toMillis() : device.lastSeen
    const timeDiff = now - lastSeenTime
    
    // Use the same 5-minute threshold as useDeviceStatusCalculation
    return timeDiff <= 5 * 60 * 1000 // 5 minutes
  }, [])

  // Memoize filtered and sorted devices to prevent unnecessary recalculations
  const filteredDevices = useMemo(() => {
    const filtered = devices.filter(device => {
      // Search across multiple fields: nickname, deviceId, and serialNumber
      const searchableFields = [
        device.nickname || '',
        device.deviceId || '',
        device.serialNumber || '',
      ]
      
      const searchableText = searchableFields.join(' ').toLowerCase()
      return searchableText.includes(searchTerm.toLowerCase())
    })
    
    // Sort devices: (1) Selected first, (2) Online/Offline, (3) Alphabetical
    return filtered.sort((a, b) => {
      // 1. Selected device always comes first
      if (a.deviceId === selectedDeviceId) return -1
      if (b.deviceId === selectedDeviceId) return 1
      
      // 2. Sort by online status (online devices first)
      const aOnline = calculateOnlineStatus(a)
      const bOnline = calculateOnlineStatus(b)
      if (aOnline !== bOnline) return aOnline ? -1 : 1
      
      // 3. Sort alphabetically by device name (nickname or deviceId)
      const aName = (a.nickname || a.deviceId || '').toLowerCase()
      const bName = (b.nickname || b.deviceId || '').toLowerCase()
      return aName.localeCompare(bName)
    })
  }, [devices, searchTerm, selectedDeviceId, calculateOnlineStatus])
  
  // Memoize handlers to prevent child re-renders
  const handleDeviceSelect = useCallback((deviceId) => {
    selectDevice(deviceId)
    setIsOpen(false)
    setSearchTerm('')
  }, [selectDevice])
  
  // TODO: Implement Fleet Overview later
  // const handleFleetView = useCallback(() => {
  //   setFleetView(true)
  //   setIsOpen(false)
  // }, [setFleetView])
  
  const toggleDropdown = useCallback(() => setIsOpen(prev => !prev), [])
  const closeDropdown = useCallback(() => setIsOpen(false), [])
  
  const handleAddDevice = useCallback(() => {
    setIsOpen(false) // Close dropdown first
    setShowPairingWizard(true) // Open pairing wizard
  }, [])
  
  const handleClosePairingWizard = useCallback(() => {
    setShowPairingWizard(false)
  }, [])
  
  // Calculate total pending items for badge
  const { useUserAccessRequests, useAdminAccessRequests, useUserInvitations } = useAccessData()
  const { data: userAccessRequests = [] } = useUserAccessRequests()
  const { data: adminAccessRequests = [] } = useAdminAccessRequests()
  const { data: userInvitations = [] } = useUserInvitations()
  
  const totalPendingCount = useMemo(() => {
    const pendingUserRequests = userAccessRequests.filter(req => req.status === 'pending').length
    const pendingAdminRequests = adminAccessRequests.length
    const pendingInvitations = userInvitations.length
    return pendingUserRequests + pendingAdminRequests + pendingInvitations
  }, [userAccessRequests, adminAccessRequests, userInvitations])

  // Outside click ref
  const dropdownRef = useRef(null)
  useOutsideClick(dropdownRef, isOpen, closeDropdown)

  // Always show dropdown experience for consistency
  return (
    <div className="dropdown" ref={dropdownRef}>
      <style>
        {`
          .device-selector-world-class {
            display: flex;
            align-items: center;
            gap: 0.625rem;
            background: var(--bg-header-gradient);
            border: 1px solid var(--border-color-dark);
            border-radius: 6px;
            padding: 0.5rem 0.75rem;
            transition: all 0.2s ease;
            cursor: pointer;
            min-width: 160px;
            font-size: 0.875rem;
            user-select: none;
            box-shadow: 0 1px 3px var(--shadow-color-light);
            animation: deviceSelectorFadeIn 0.4s ease-out;
          }
          
          .device-selector-world-class:hover {
            border-color: var(--bs-primary);
            box-shadow: 0 2px 8px var(--color-hover-medium);
            transform: translateY(-1px);
            background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
          }
          
          .device-selector-world-class:focus {
            outline: 2px solid var(--color-focus-ring);
            outline-offset: 2px;
            box-shadow: 0 2px 12px rgba(var(--bs-primary-rgb), 0.2);
            border-color: var(--bs-primary);
          }
          
          .device-name-world-class {
            font-weight: 600;
            color: var(--text-primary);
            line-height: 1.3;
            animation: deviceNameSlideIn 0.3s ease-out 0.1s both;
          }
          
          .device-status-world-class {
            font-size: 0.7rem;
            color: var(--text-muted);
            line-height: 1.2;
            animation: deviceStatusSlideIn 0.3s ease-out 0.15s both;
          }
          
          .device-icon-world-class {
            animation: deviceIconScale 0.3s ease-out;
          }
          
          .chevron-world-class {
            transition: transform 0.2s ease;
            color: var(--text-muted);
          }
          
          .device-selector-world-class:hover .chevron-world-class {
            color: var(--bs-primary);
          }
          
          .device-selector-world-class[aria-expanded="true"] .chevron-world-class {
            transform: rotate(180deg);
            color: var(--bs-primary);
          }
          
          .dropdown-menu-world-class {
            border: 1px solid var(--border-color-dark);
            border-radius: 12px;
            box-shadow: 0 8px 32px var(--shadow-color-medium);
            animation: dropdownSlideIn 0.2s ease-out;
            backdrop-filter: blur(8px);
            background: rgba(255, 255, 255, 0.95);
          }
          
          .device-item-world-class {
            transition: all 0.2s ease;
            border-radius: 8px;
            margin: 0.1875rem;
            padding: 0.8125rem;
            border: 1px solid transparent;
          }
          
          .device-item-world-class:hover {
            background: var(--nav-hover-bg);
            border-color: rgba(var(--bs-primary-rgb), 0.2);
            transform: translateX(1px);
          }
          
          .device-item-world-class.selected {
            background: var(--nav-active-bg);
            color: white;
            border-color: var(--bs-primary-dark);
            box-shadow: 0 2px 8px var(--color-active-shadow);
          }
          
          .device-item-world-class.selected .text-muted {
            color: var(--text-light-80) !important;
          }
          
          .device-item-world-class.selected .small {
            color: var(--text-light-90) !important;
          }
          
          @keyframes deviceSelectorFadeIn {
            from {
              opacity: 0;
              transform: translateY(-4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes deviceNameSlideIn {
            from {
              opacity: 0;
              transform: translateX(-8px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes deviceStatusSlideIn {
            from {
              opacity: 0;
              transform: translateX(-6px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes deviceIconScale {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes dropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-8px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <button
        className="device-selector-world-class"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
      >
        <img 
          src="/images/Altion-final.svg" 
          alt="Altion Device" 
          width="32" 
          height="32"
          className="device-icon-world-class"
          style={{ flexShrink: 0 }}
          key={selectedDeviceId}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="device-name-world-class" key={`name-${selectedDeviceId}`}>{currentDeviceName}</div>
          <div className="device-status-world-class" key={`status-${selectedDeviceId}`}>
            {hasMultipleDevices ? 'Select Device' : 'Device & Access'}
          </div>
        </div>
        <div className="d-flex align-items-center ms-auto">
          {totalPendingCount > 0 && (
            <span className="badge bg-primary text-light me-2">{totalPendingCount}</span>
          )}
          <i className="bi bi-chevron-down chevron-world-class"></i>
        </div>
      </button>
      
      {isOpen && (
        <>
          <div className="dropdown-menu show dropdown-menu-world-class" style={{ minWidth: '420px', maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Search Bar for device filtering - only show if multiple devices */}
            {hasMultipleDevices && (
              <>
                <div className="p-3">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by name or device ID (serial number)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ 
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      padding: '0.625rem 0.875rem',
                    }}
                  />
                </div>
                
                <div className="dropdown-divider"></div>
              </>
            )}
            
            {/* Device List */}
            <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
              {filteredDevices.map((device) => (
                <DeviceListItem
                  key={device.deviceId}
                  device={device}
                  isSelected={device.deviceId === selectedDeviceId}
                  onSelect={handleDeviceSelect}
                />
              ))}
            </div>
            
            {/* Device Invitations Section */}
            <DeviceInvitationsSection />
            
            {/* Pending Access Requests Section */}
            <PendingAccessRequestsSection />
            
            <div className="dropdown-divider"></div>
            
            {/* Global Actions */}
            <div className="p-3 bg-light bg-opacity-25" style={{ borderRadius: '0 0 12px 12px' }}>
              {/* TODO: Implement Fleet Overview later
              Fleet Overview - only show for 3+ devices
              {devices.length >= 3 && (
                <button 
                  className="btn btn-sm btn-outline-info w-100 mb-2"
                  onClick={handleFleetView}
                  style={{ 
                    borderRadius: '8px',
                    padding: '0.625rem 1rem',
                    fontWeight: '500',
                  }}
                >
                  <i className="bi bi-collection me-2"></i>
                  Fleet Overview
                </button>
              )}
              */}
              
              {/* Add Device - more prominent styling */}
              <button 
                className="btn btn-sm btn-success w-100 fw-semibold"
                onClick={handleAddDevice}
                style={{ 
                  borderRadius: '8px',
                  padding: '0.625rem 1rem',
                  fontWeight: '600',
                }}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add Device
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Device Pairing Wizard Modal */}
      <DevicePairingWizard 
        isOpen={showPairingWizard} 
        onClose={handleClosePairingWizard} 
      />
    </div>
  )
}

// Memoized device list item component to prevent unnecessary re-renders
function DeviceListItem({ device, isSelected, onSelect }) {
  // Use admin-only requests hook and count only this device's requests
  const { useAdminAccessRequests } = useAccessData()
  const { data: adminAccessRequests = [] } = useAdminAccessRequests()
  const pendingCount = adminAccessRequests.filter(req => req.deviceId === device.deviceId).length

  // Get real-time device status for accurate connection status
  const { status } = useDeviceStatus(device.deviceId)
  const { isOnline } = useDeviceStatusCalculation(status)

  return (
    <button
      className={`dropdown-item d-flex align-items-center device-item-world-class ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(device.deviceId)}
      style={{ 
        border: 'none', 
        width: 'calc(100% - 0.375rem)',
        minWidth: 0,
      }}
    >
      {/* Device Icon */}
      <div className="me-3" style={{ flexShrink: 0 }}>
        <div 
          className={`rounded-circle d-flex align-items-center justify-content-center ${
            isOnline ? 'bg-light border border-success' : 'bg-light border border-secondary'
          }`}
          style={{ width: '36px', height: '36px' }}
        >
          <img 
            src="/images/Altion-final.svg" 
            alt="Altion Device" 
            width="22" 
            height="22"
          />
        </div>
      </div>
      
      {/* Device Information - Simple left/right layout */}
      <div className="flex-grow-1 text-start">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div className="fw-semibold text-truncate" style={{ maxWidth: '250px' }}>
            {device.nickname || device.deviceId}
          </div>
          <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
            <span 
              className={`badge rounded-pill ${
                isOnline ? 'bg-success' : 'bg-secondary'
              }`}
              style={{ fontSize: '0.7rem' }}
            >
              {isOnline ? 'Online' : 'Offline'}
            </span>
            {pendingCount > 0 && (
              <span className="badge bg-danger text-light ms-2" style={{ fontSize: '0.7rem' }}>
                {pendingCount}
              </span>
            )}
          </div>
        </div>
        
        {/* Device details */}
        <div className="small text-muted">
          {device.serialNumber && (
            <div className="text-truncate" style={{ maxWidth: '320px' }}>
              <i className="bi bi-hash me-1"></i>
              SN: {device.serialNumber}
            </div>
          )}
          {device.location && (
            <div className="text-truncate" style={{ maxWidth: '320px' }}>
              <i className="bi bi-geo-alt me-1"></i>
              {device.location}
            </div>
          )}
          {!device.serialNumber && !device.location && (
            <div className="text-truncate" style={{ maxWidth: '320px' }}>
              <i className="bi bi-device-hdd me-1"></i>
              {device.deviceId}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

DeviceListItem.propTypes = {
  device: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    nickname: PropTypes.string,
    serialNumber: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
}

// Component to show pending access requests
function PendingAccessRequestsSection() {
  const { useUserAccessRequests } = useAccessData()
  const { revokeAccessRequest, isLoading } = useAccessManagement()
  const { data: userAccessRequests = [], isLoading: requestsLoading } = useUserAccessRequests()
  
  // Filter to only pending requests
  const pendingRequests = userAccessRequests.filter(req => req.status === 'pending')
  
  const handleRevokeRequest = useCallback(async (requestId, _deviceId) => {
    try {
      await revokeAccessRequest(requestId)
      // Success feedback could be added here if needed
    } catch (error) {
      console.error('Failed to revoke access request:', error)
      // Error feedback could be added here if needed
    }
  }, [revokeAccessRequest])
  
  // Don't show section if no pending requests
  if (requestsLoading || pendingRequests.length === 0) {
    return null
  }
  
  return (
    <>
      <div className="dropdown-divider"></div>
      <div className="px-3 py-2">
        <h6 className="text-muted mb-2 small d-flex align-items-center justify-content-between">
          <span>
            <i className="bi bi-clock me-1"></i>
            Pending Access Requests
          </span>
          <span className="badge bg-warning text-dark">{pendingRequests.length}</span>
        </h6>
        <div style={{ maxHeight: '150px', overflowY: 'auto', overflowX: 'hidden' }}>
          {pendingRequests.map((request) => (
            <PendingAccessRequestItem
              key={request.id}
              request={request}
              onRevoke={handleRevokeRequest}
              isLoading={isLoading}
            />
          ))}
        </div>
      </div>
    </>
  )
}

// Individual pending access request item
function PendingAccessRequestItem({ request, onRevoke, isLoading }) {
  const handleRevoke = useCallback(() => {
    onRevoke(request.id, request.deviceId)
  }, [onRevoke, request.id, request.deviceId])
  
  const formatRequestDate = (timestamp) => {
    if (!timestamp) return 'Unknown Date'
    
    try {
      // Handle Firestore Timestamp objects
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch (_error) {
      return 'Invalid Date'
    }
  }
  
  return (
    <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded small access-request-item-subtle">
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-medium text-truncate" style={{ maxWidth: '200px' }}>
          {request.deviceId}
        </div>
        <div className="text-muted small">
          Requested {formatRequestDate(request.createdAt)}
        </div>
      </div>
      <button
        className="btn btn-sm btn-outline-danger"
        onClick={handleRevoke}
        disabled={isLoading}
        title="Revoke this access request"
      >
        {isLoading ? (
          <i className="bi bi-hourglass-split"></i>
        ) : (
          <i className="bi bi-x"></i>
        )}
      </button>
    </div>
  )
}

PendingAccessRequestItem.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    deviceId: PropTypes.string.isRequired,
    requestedAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object, // Firebase Timestamp
    ]).isRequired,
  }).isRequired,
  onRevoke: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

// Component to show user's pending device invitations
function DeviceInvitationsSection() {
  const { useUserInvitations, getUserInfo } = useAccessData()
  const { acceptInvitation, declineInvitation, isLoading } = useAccessManagement()
  const { data: userInvitations = [], isLoading: invitationsLoading } = useUserInvitations()
  const [inviterMap, setInviterMap] = useState({})
  
  // Load inviter names for display
  useEffect(() => {
    userInvitations?.forEach(inv => {
      if (inv.invitedBy && !inviterMap[inv.id]) {
        getUserInfo(inv.invitedBy).then(user => {
          const name = user?.displayName || user?.name || user?.email || inv.invitedBy
          setInviterMap(prev => ({ ...prev, [inv.id]: name }))
        })
      }
    })
  }, [userInvitations, getUserInfo, inviterMap])
  
  const handleAcceptInvitation = useCallback(async (invitationId, _deviceId, _inviterName) => {
    try {
      await acceptInvitation(invitationId)
      // Could add success feedback here if needed
    } catch (error) {
      console.error('Failed to accept invitation:', error)
      // Could add error feedback here if needed
    }
  }, [acceptInvitation])

  const handleDeclineInvitation = useCallback(async (invitationId, _deviceId, _inviterName) => {
    try {
      await declineInvitation(invitationId)
      // Could add feedback here if needed
    } catch (error) {
      console.error('Failed to decline invitation:', error)
      // Could add error feedback here if needed
    }
  }, [declineInvitation])
  
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return 'Never'
    const date = timestamp?.toDate?.() ?? new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }, [])
  
  // Don't show section if no invitations or loading
  if (invitationsLoading || userInvitations.length === 0) {
    return null
  }
  
  return (
    <>
      <div className="dropdown-divider"></div>
      <div className="px-3 py-2">
        <h6 className="text-muted mb-2 small d-flex align-items-center justify-content-between">
          <span>
            <i className="bi bi-envelope-heart me-1"></i>
            Device Invitations
          </span>
          <span className="badge bg-info text-dark">{userInvitations.length}</span>
        </h6>
        <div style={{ maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden' }}>
          {userInvitations.map((invitation) => (
            <DeviceInvitationItem
              key={invitation.id}
              invitation={invitation}
              inviterName={inviterMap[invitation.id] || invitation.invitedBy}
              onAccept={handleAcceptInvitation}
              onDecline={handleDeclineInvitation}
              isLoading={isLoading}
              formatTimestamp={formatTimestamp}
            />
          ))}
        </div>
      </div>
    </>
  )
}

// Individual device invitation item
function DeviceInvitationItem({ invitation, inviterName, onAccept, onDecline, isLoading, formatTimestamp }) {
  const handleAccept = useCallback(() => {
    onAccept(invitation.id, invitation.deviceId, inviterName)
  }, [onAccept, invitation.id, invitation.deviceId, inviterName])
  
  const handleDecline = useCallback(() => {
    onDecline(invitation.id, invitation.deviceId, inviterName)
  }, [onDecline, invitation.id, invitation.deviceId, inviterName])
  
  return (
    <div className="mb-3 p-2 border rounded small access-request-item-subtle">
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          <div className="fw-medium text-success">
            <i className="bi bi-envelope-heart me-1"></i>
            Device Access Invitation
          </div>
          <div className="text-muted small mt-1">
            Invited by {inviterName} to access device <strong>{invitation.deviceId}</strong>
          </div>
          <div className="text-muted small">
            Permission level: <span className={`badge ${
              invitation.permission === 'admin' ? 'bg-danger' : 
              invitation.permission === 'technician' ? 'bg-warning text-dark' : 'bg-secondary'
            }`}>
              {invitation.permission}
            </span>
          </div>
          <small className="text-muted">
            Invited: {formatTimestamp(invitation.invitedAt || invitation.createdAt)}
            {invitation.expiresAt && (
              <span className="ms-2">
                • Expires: {formatTimestamp(invitation.expiresAt)}
              </span>
            )}
          </small>
        </div>
      </div>
      
      <div className="d-flex gap-2 mt-2">
        <button 
          className="btn btn-sm btn-success flex-fill"
          onClick={handleAccept}
          disabled={isLoading}
          title="Accept invitation"
        >
          <i className="bi bi-check-lg me-1"></i>
          Accept
        </button>
        <button 
          className="btn btn-sm btn-outline-danger flex-fill"
          onClick={handleDecline}
          disabled={isLoading}
          title="Decline invitation"
        >
          <i className="bi bi-x-lg me-1"></i>
          Decline
        </button>
      </div>
    </div>
  )
}

DeviceInvitationItem.propTypes = {
  invitation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    deviceId: PropTypes.string.isRequired,
    permission: PropTypes.string.isRequired,
    invitedAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object, // Firebase Timestamp
    ]),
    createdAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object, // Firebase Timestamp
    ]),
    expiresAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.object, // Firebase Timestamp
    ]),
  }).isRequired,
  inviterName: PropTypes.string.isRequired,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  formatTimestamp: PropTypes.func.isRequired,
}

export default DeviceSelector
